<!-- =========================================================================================
  File Name: WizardStepDemand.vue
  Description: Step 3 - Configure demand for points (optional but recommended)
  ----------------------------------------------------------------------------------------
  Author: Serviroutes Team
  Version: 2025-10-30
========================================================================================== -->

<template>
	<div class="wizard-step-demand">
		<div class="step-header">
			<h2 class="step-title">{{ $t("Wizard.Steps.Demand.MainTitle") }}</h2>
			<p class="step-description">{{ $t("Wizard.Steps.Demand.Description") }}</p>
		</div>
		<div class="step-content">
			<!-- Quick Demand Configuration -->
			<div class="quick-demand-section">
				<vx-card>
					<h3 class="section-title">{{ $t("Wizard.Steps.Demand.QuickSetup") }}</h3>
					<p class="section-description">{{ $t("Wizard.Steps.Demand.QuickHelp") }}</p>

					<div class="quick-demand-controls">
						<vs-input-number
							v-model="quickDemandValue"
							:label="$t('Clustering.DefaultDemand')"
							:min="0"
							:max="999999"
							class="demand-input"
						/>
						<vs-button
							color="success"
							type="gradient"
							icon-pack="feather"
							icon="icon-zap"
							:disabled="isApplying"
							@click="applyQuickDemand"
						>
							{{ $t("Clustering.ApplyToAll") }}
						</vs-button>
					</div>
				</vx-card>
			</div>

			<!-- Individual Point Demand Editor -->
			<div class="points-editor-section">
				<vx-card>
					<div class="editor-header">
						<h3 class="section-title">{{ $t("Wizard.Steps.Demand.IndividualEdit") }}</h3>
						<div class="editor-actions">
							<vs-button
								size="small"
								color="danger"
								type="border"
								icon-pack="feather"
								icon="icon-trash-2"
								:disabled="totalDemand === 0"
								@click="resetDemands"
							>
								{{ $t("Clustering.ResetDemands") }}
							</vs-button>
						</div>
					</div>

					<!-- Points Table -->
					<div class="points-table-container">
						<div class="table-controls">
							<vs-input
								v-model="searchQuery"
								icon-pack="feather"
								icon="icon-search"
								:placeholder="$t('Search')"
								class="search-input"
							/>
							<span class="points-count">
								{{ pointsWithDemand }}/{{ wizardData.selectedPoints.length }}
								{{ $t("Clustering.PointsWithDemand") }}
							</span>
						</div>

						<div class="points-table-wrapper">
							<table class="points-table">
								<caption></caption>
								<thead>
									<tr>
										<th style="width: 50px">#</th>
										<th>{{ $t("Identification") }}</th>
										<th>{{ $t("Clustering.Latitude") }}</th>
										<th>{{ $t("Clustering.Longitude") }}</th>
										<th style="width: 150px">{{ $t("Clustering.Demand") }}</th>
										<th style="width: 80px">{{ $t("Routing.Table.Actions") }}</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="(point, index) in paginatedPoints"
										:key="point.id || index"
										:class="{'has-demand': (point.demand || 0) > 0}"
									>
										<td>{{ (currentPage - 1) * pointsPerPage + index + 1 }}</td>
										<td>
											<div class="point-identification">
												<feather-icon
													icon="MapPinIcon"
													svgClasses="w-4 h-4"
													class="point-icon"
												/>
												<strong>{{ point.identification || point.name || "N/A" }}</strong>
											</div>
										</td>
										<td>
											<span class="point-address">{{ point.latitude || "N/A" }}</span>
										</td>
										<td>
											<span class="point-address">{{ point.longitude || "N/A" }}</span>
										</td>
										<td>
											<vs-input-number
												v-model.number="point.demand"
												:min="0"
												:max="999999"
												size="small"
												@input="updatePointDemand(point)"
											/>
										</td>
										<td>
											<div class="action-buttons">
												<vs-button
													size="small"
													color="primary"
													type="flat"
													icon-pack="feather"
													icon="icon-map-pin"
													:title="$t('Clustering.ZoomToPoint')"
													@click="zoomToPoint(point)"
												/>
											</div>
										</td>
									</tr>
								</tbody>
							</table>
						</div>

						<!-- Pagination -->
						<div class="table-pagination">
							<vs-pagination v-model="currentPage" :total="totalPages" :max="7" />
							<div class="pagination-info">
								{{
									$t("Clustering.ShowingItems", {
										start: startIndex + 1,
										end: endIndex,
										total: filteredPoints.length + " " + $t("Clustering.SelectedPoints"),
									})
								}}
							</div>
						</div>
					</div>
				</vx-card>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "WizardStepDemand",
	props: {
		wizardData: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			quickDemandValue: 1,
			isApplying: false,
			searchQuery: "",
			currentPage: 1,
			pointsPerPage: 10,
		};
	},
	computed: {
		totalDemand() {
			return this.wizardData.selectedPoints.reduce((sum, p) => sum + (parseInt(p.demand) || 0), 0);
		},
		pointsWithDemand() {
			return this.wizardData.selectedPoints.filter((p) => (parseInt(p.demand) || 0) > 0).length;
		},
		filteredPoints() {
			if (!this.searchQuery) {
				return this.wizardData.selectedPoints;
			}

			const query = this.searchQuery.toLowerCase();
			return this.wizardData.selectedPoints.filter((point) => {
				const identification = (point.identification || point.name || "").toLowerCase();
				const address = (point.address1 || point.address || "").toLowerCase();
				return identification.includes(query) || address.includes(query);
			});
		},
		totalPages() {
			return Math.ceil(this.filteredPoints.length / this.pointsPerPage);
		},
		paginatedPoints() {
			const start = (this.currentPage - 1) * this.pointsPerPage;
			const end = start + this.pointsPerPage;
			return this.filteredPoints.slice(start, end);
		},
		startIndex() {
			return (this.currentPage - 1) * this.pointsPerPage;
		},
		endIndex() {
			return Math.min(this.startIndex + this.pointsPerPage, this.filteredPoints.length);
		},
	},
	watch: {
		searchQuery() {
			// Reset to first page when searching
			this.currentPage = 1;
		},
	},
	methods: {
		async applyQuickDemand() {
			this.isApplying = true;

			const updatedPoints = this.wizardData.selectedPoints.map((point) => ({
				...point,
				demand: parseInt(this.quickDemandValue) || 0,
			}));

			this.$emit("update-data", {
				selectedPoints: updatedPoints,
				demandConfigured: true,
				totalDemand: this.quickDemandValue * updatedPoints.length,
			});
			await new Promise((resolve) => setTimeout(resolve, 500));
			this.isApplying = false;
		},
		updatePointDemand(point) {
			// Ensure demand is not negative
			if (point.demand < 0) {
				point.demand = 0;
			}

			// Update point demand in real-time
			const updatedPoints = this.wizardData.selectedPoints.map((p) => {
				if (p.id === point.id || (p.latitude === point.latitude && p.longitude === point.longitude)) {
					return {...p, demand: parseInt(point.demand) || 0};
				}
				return p;
			});
			const newTotalDemand = updatedPoints.reduce((sum, p) => sum + (parseInt(p.demand) || 0), 0);
			this.$emit("update-data", {
				selectedPoints: updatedPoints,
				demandConfigured: updatedPoints.some((p) => (parseInt(p.demand) || 0) > 0),
				totalDemand: newTotalDemand,
			});
		},
		zoomToPoint(point) {
			// Emit event to parent to zoom to specific point
			this.$emit("zoom-to-point", point);
		},
		async resetDemands() {
			const updatedPoints = this.wizardData.selectedPoints.map((point) => ({
				...point,
				demand: 0,
			}));

			this.$emit("update-data", {
				selectedPoints: updatedPoints,
				demandConfigured: false,
				totalDemand: 0,
			});

			this.$vs.notify({
				color: "warning",
				title: this.$t("Information"),
				text: this.$t("Clustering.DemandsReset"),
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.wizard-step-demand {
	.step-header {
		text-align: center;
		margin-bottom: 12px;

		.step-title {
			font-size: 1.5rem;
			font-weight: 700;
			color: #333;
			margin-bottom: 8px;
		}

		.step-description {
			font-size: 0.95rem;
			color: #666;
			margin-bottom: 8px;
		}

		.step-note {
			color: rgba(var(--vs-success), 1);
			font-weight: 600;
		}
	}

	.step-content {
		.quick-demand-section {
			margin-bottom: 12px;

			.section-title {
				font-size: 1.5rem;
				margin-bottom: 8px;
			}

			.section-description {
				color: #666;
				margin-bottom: 24px;
			}

			.quick-demand-controls {
				display: flex;
				gap: 16px;
				align-items: flex-end;

				.demand-input {
					flex: 1;
					max-width: 300px;
				}
			}
		}

		.points-editor-section {
			margin-bottom: 24px;

			.editor-header {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-bottom: 24px;
				flex-wrap: wrap;
				gap: 12px;

				.section-title {
					font-size: 1.2rem;
					font-weight: 600;
					margin: 0;
				}

				.editor-actions {
					display: flex;
					gap: 8px;
				}
			}

			.points-table-container {
				.table-controls {
					display: flex;
					justify-content: space-between;
					align-items: center;
					margin-bottom: 16px;
					gap: 16px;

					.search-input {
						flex: 1;
						max-width: 400px;
					}

					.points-count {
						font-weight: 600;
						color: #666;
						white-space: nowrap;
					}
				}

				.points-table-wrapper {
					overflow-x: auto;
					border: 1px solid #e0e0e0;
					border-radius: 8px;
					margin-bottom: 16px;

					.points-table {
						width: 100%;
						border-collapse: collapse;
						background: white;

						thead {
							background: #f5f7fa;

							tr {
								th {
									padding: 12px 16px;
									text-align: left;
									font-weight: 600;
									color: #333;
									border-bottom: 2px solid #e0e0e0;
									white-space: nowrap;
								}
							}
						}

						tbody {
							tr {
								transition: background-color 0.2s ease;

								&:hover {
									background-color: #f9fafb;
								}

								&.has-demand {
									background-color: #e8f5e9;
								}

								td {
									padding: 12px 16px;
									border-bottom: 1px solid #e0e0e0;
									vertical-align: middle;

									.point-identification {
										display: flex;
										align-items: center;
										gap: 8px;

										.point-icon {
											color: #4caf50;
										}

										strong {
											color: #333;
										}
									}

									.point-address {
										color: #666;
										font-size: 0.9rem;
									}

									.action-buttons {
										display: flex;
										gap: 4px;
										justify-content: center;
									}
								}
							}
						}
					}
				}

				.table-pagination {
					display: flex;
					align-items: center;
					justify-content: center;
					flex-wrap: wrap;
					gap: 16px;

					.pagination-info {
						color: #666;
						font-size: 0.9rem;
					}
				}
			}
		}

		.skip-info {
			margin-top: 32px;
		}
	}
}

@media screen and (max-width: 768px) {
	.wizard-step-demand {
		.points-editor-section {
			.editor-header {
				flex-direction: column;
				align-items: flex-start;

				.editor-actions {
					width: 100%;
					justify-content: flex-start;
				}
			}

			.points-table-container {
				.table-controls {
					flex-direction: column;
					align-items: stretch;

					.search-input {
						max-width: 100%;
					}
				}

				.points-table-wrapper {
					.points-table {
						font-size: 0.85rem;

						thead tr th,
						tbody tr td {
							padding: 8px 12px;
						}
					}
				}

				.table-pagination {
					flex-direction: column;
					align-items: center;
				}
			}
		}
	}
}
</style>
